import Header from "@/components/header";
import useIsSmall from "@/hooks/useIsSmall";
import TodaySuggestions from "@/modules/smartAudience/TodaySuggestions";
import { useGetCustomerNotifier } from "@/services/autoMessage/hooks/useGetCustomerNotifier";
import {
  IconAlignBoxRightMiddle,
  IconChevronLeft,
  IconDiscount2,
  IconHistory,
  IconMessageBolt,
  IconStar,
  IconUsers,
  IconUsersGroup,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import classes from "./marketing.module.css";

const fa = (value: number) => value.toLocaleString("fa-IR");

// Marketing hub, laid out like Fresha's and Square's: start a campaign by goal, today's suggestions,
// the automations and their state, then the tools (history, templates, groups, reviews).
const campaigns = [
  { to: "/dashboard/messages/selectUser", icon: IconUsers, title: "پیام به مشتری‌ها", text: "خبر، یادآوری یا تبریک؛ به همه یا یک گروه.", tone: "violet" },
  { to: "/dashboard/discount/selectGroup", icon: IconDiscount2, title: "پیامک با کد تخفیف", text: "هر نفر یک کد؛ برای برگرداندن و فروش بیشتر.", tone: "green" },
] as const;

const tools = [
  { to: "/dashboard/messages", icon: IconHistory, title: "تاریخچه پیامک‌ها", text: "ارسال‌ها و وضعیتشان" },
  { to: "/dashboard/profile/template", icon: IconAlignBoxRightMiddle, title: "قالب‌های من", text: "متن‌هایی که خودتان نوشته‌اید" },
  { to: "/dashboard/profile/customerGroup", icon: IconUsersGroup, title: "گروه‌های پیامکی", text: "مشتری‌ها را دسته‌بندی کنید" },
  { to: "/dashboard/reviews", icon: IconStar, title: "نظرات مشتریان", text: "امتیازها و نظرهای ثبت‌شده" },
];

function Marketing() {
  const isSmall = useIsSmall();
  const notifiers = useGetCustomerNotifier();
  const automations = notifiers.data ?? [];
  const activeCount = automations.filter((n) => n.isActive).length;

  return (
    <>
      {isSmall && <Header title="بازاریابی" showBack route="/dashboard" />}
      <main className={classes.page} dir="rtl">
        {!isSmall && (
          <header className={classes.intro}>
            <h1>بازاریابی</h1>
            <p>مشتری‌ها را برگردانید، وقت‌های خالی را پر کنید و ارتباطتان را خودکار کنید.</p>
          </header>
        )}

        <section className={classes.section} aria-labelledby="mk-start">
          <h2 id="mk-start">کمپین جدید</h2>
          <div className={classes.campaigns}>
            {campaigns.map(({ to, icon: Icon, title, text, tone }) => (
              <Link key={to} to={to} className={classes.campaign} data-tone={tone}>
                <span className={classes.campaignIcon}><Icon size={24} stroke={1.7} /></span>
                <strong>{title}</strong>
                <span>{text}</span>
              </Link>
            ))}
          </div>
        </section>

        <TodaySuggestions />

        <Link to="/dashboard/profile/autoMessage" className={classes.automation}>
          <span className={classes.automationIcon}><IconMessageBolt size={24} stroke={1.7} /></span>
          <span className={classes.automationText}>
            <strong>پیامک‌های خودکار</strong>
            <span>خوش‌آمدگویی، تأیید نوبت و خبر نوبت آنلاین؛ بدون کار اضافه.</span>
          </span>
          {!notifiers.isLoading && automations.length > 0 && (
            <span className={classes.status} data-on={activeCount > 0}>
              {activeCount > 0 ? `${fa(activeCount)} از ${fa(automations.length)} فعال` : "خاموش"}
            </span>
          )}
          <IconChevronLeft size={18} className={classes.chevron} />
        </Link>

        <section className={classes.section} aria-labelledby="mk-tools">
          <h2 id="mk-tools">ابزارها</h2>
          <div className={classes.tools}>
            {tools.map(({ to, icon: Icon, title, text }) => (
              <Link key={to} to={to} className={classes.tool}>
                <span className={classes.toolIcon}><Icon size={20} stroke={1.7} /></span>
                <span className={classes.toolText}>
                  <strong>{title}</strong>
                  <span>{text}</span>
                </span>
                <IconChevronLeft size={16} className={classes.chevron} />
              </Link>
            ))}
          </div>
        </section>
      </main>
    </>
  );
}

export default Marketing;
